import type { ToolApprovalSibling } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { CardAvatar, SentText } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { useNowUntilExpiry } from "#features/conversations/hooks/useNowUntilExpiry.ts";
import { attendeesOf, availabilityExpiry, availabilityLine, formatEventTime, overlappingInvite, providerOf, recurrenceLabel, type Attendee } from "#features/conversations/components/prepared-action/calendarEventFields.ts";
import { readBoolean, readString, type Fields } from "#features/conversations/lib/untypedFields.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Text } from "@hopper-ui/components";
import { IconClock, IconLocation, IconPeople, IconRepeat, IconTriangleExclamation, IconVideoCam } from "@intentui/icons";
import classNames from "classnames";
import type { ReactNode } from "react";

interface Props {
    args: Fields;
    display: Fields;
    // An answered invite shows what was booked, so the check made before the decision no longer applies.
    isSettled: boolean;
    // The editor shows the description in its own field.
    hidesDescription?: boolean;
    // The batch's other invites, to warn when this one overlaps one.
    siblings?: ToolApprovalSibling[];
    // Replaces the online-meeting row, which the editor turns into a switch.
    onlineControl?: ReactNode;
    // Replaces the time text, which the editor turns into date and time inputs.
    whenControl?: ReactNode;
    // The card's times differ from the ones core checked, so its availability does not apply. The check's expiry still runs.
    hidesAvailability?: boolean;
}

export function DetailRow({ glyph, children }: { glyph: ReactNode; children: ReactNode }) {
    return (
        <div className="wai-ce-row">
            <span className="wai-ce-row-glyph" aria-hidden="true">{glyph}</span>
            <div className="wai-ce-row-body">{children}</div>
        </div>
    );
}

function AttendeeChip({ label, photoUrl, isWarning }: Attendee) {
    return (
        <span className={classNames("wai-ce-chip", isWarning && "wai-ce-chip-warning")}>
            {isWarning ? <IconTriangleExclamation className="wai-ce-chip-warning-glyph" aria-hidden="true" /> : <CardAvatar name={label} photoUrl={photoUrl} />}
            <Text size="xs" fontWeight="body-xs-medium" color="neutral">{label}</Text>
        </span>
    );
}

export function AttendeeChips({ attendees }: { attendees: Attendee[] }) {
    // The list is static and keeps core's order; two attendees can read alike, so the label is no key.
    // eslint-disable-next-line react/no-array-index-key
    return <div className="wai-ce-chips">{attendees.map((attendee, index) => <AttendeeChip key={index} {...attendee} />)}</div>;
}

// Every value shows as plain text: the arguments are the model's (renderer spec R6).
export function CalendarEventReadView({ args, display, isSettled, hidesDescription = false, siblings = [], onlineControl, whenControl, hidesAvailability = false }: Props) {
    const { tShared, i18n } = useSharedTranslation();
    const provider = providerOf(args);
    // The editor's inputs replace the text, so there is nothing to format while they show.
    const when = whenControl === undefined ? formatEventTime(args, i18n.language) : null;
    const attendees = attendeesOf(args, display, tShared);
    const now = useNowUntilExpiry(availabilityExpiry(display));
    const overlap = isSettled ? null : overlappingInvite(args, siblings);
    const availability = isSettled ? null : availabilityLine(hidesAvailability ? { ...display, availability: undefined } : display, tShared, i18n.language, now, overlap)?.text ?? null;
    const location = readString(args, "location");
    const recurrence = recurrenceLabel(args, tShared);
    const description = readString(args, "description");
    const onlineKey = provider === "outlook" ? "calendarEvent.onlineOutlook" : provider === "google" ? "calendarEvent.onlineGoogle" : "calendarEvent.online";

    return (
        <>
            {whenControl !== undefined
                ? <DetailRow glyph={<IconClock />}>{whenControl}</DetailRow>
                : when !== null && <DetailRow glyph={<IconClock />}><Text size="sm">{when}</Text></DetailRow>}
            {(attendees.length > 0 || availability !== null) && (
                <DetailRow glyph={<IconPeople />}>
                    {attendees.length > 0 && <AttendeeChips attendees={attendees} />}
                    {availability !== null && <Text size="xs" className="wai-pa-muted" role="status">{availability}</Text>}
                </DetailRow>
            )}
            {onlineControl !== undefined
                ? <DetailRow glyph={<IconVideoCam />}>{onlineControl}</DetailRow>
                : readBoolean(args, "is_online_event", true) && <DetailRow glyph={<IconVideoCam />}><Text size="sm">{tShared(onlineKey)}</Text></DetailRow>}
            {location !== null && <DetailRow glyph={<IconLocation />}><Text size="sm">{location}</Text></DetailRow>}
            {recurrence !== null && <DetailRow glyph={<IconRepeat />}><Text size="sm">{recurrence}</Text></DetailRow>}
            {description !== null && !hidesDescription && <SentText>{description}</SentText>}
        </>
    );
}
